import { type FC, useEffect } from 'react'
import { useSessionStorage } from 'usehooks-ts'

import { type CmsComponentStickyBanner } from '@redteclab/api/clients/content-management-system'
import { IconClose } from '@redteclab/icons'

import { Button } from 'base-ui'

import { StickyBannerCountdown } from './StickyBannerCountdown'
import { StickyBannerVoucherCopy } from './StickyBannerVoucherCopy'

export const StickyBannerBody: FC<{
  banner: CmsComponentStickyBanner
}> = ({ banner }) => {
  const [isBanerHidden, setBannerHidden] = useSessionStorage(banner.id, false, {
    initializeWithValue: false,
  })

  useEffect(() => {
    const body = document.querySelector('body')

    if (!body) {
      return undefined
    }

    if (!isBanerHidden) {
      body.classList.add('withNlBanner')
    }

    return (): void => {
      body.classList.remove('withNlBanner')
    }
  }, [isBanerHidden])

  if (isBanerHidden) {
    return null
  }

  const {
    accentColor,
    backgroundColor,
    color,
    endDate,
    imgAlt,
    imgUrl,
    linkText,
    linkUrl,
    mobileSubtitle,
    subtitle,
    title,
    type,
    voucherCode,
  } = banner

  const isCountdownBanner = type === 'countdown'

  return (
    <div
      className="fixed bottom-0 z-10 w-full bg-dark-pressed-accent"
      data-clientside-hook="stickyBanner"
      /**
       * we have sticky banners that are added by e.g. GTM.
       * this banners e.g. visibility is explictly controlled by state context.
       * to distinguish between them we add this attribute - so it can be excluded by selectors
       */
      data-controlled-by-react="true"
      style={{ backgroundColor, color }}
    >
      <div className="container relative flex items-center">
        {/* eslint-disable-next-line @next/next/no-img-element -- we don't know the image size */}
        <img
          alt={imgAlt}
          className="hidden max-h-[90px] tablet:block"
          loading="lazy"
          src={imgUrl}
        />
        <div className="flex w-full justify-center py-5 pr-5 tablet:py-2">
          <div className="flex flex-col items-center gap-3 tablet:flex-row tablet:gap-10">
            <div className="flex flex-col items-center justify-center">
              <h3
                className="text-center text-2xl"
                // eslint-disable-next-line react/no-danger -- data was sanitized on server side
                dangerouslySetInnerHTML={{
                  __html: title,
                }}
              />
              <div
                className="hidden text-center mobile:flex"
                // eslint-disable-next-line react/no-danger -- data was sanitized on server side
                dangerouslySetInnerHTML={{ __html: subtitle }}
                style={{ color: accentColor }}
              />
              <div
                className="text-center mobile:hidden"
                // eslint-disable-next-line react/no-danger -- data was sanitized on server side
                dangerouslySetInnerHTML={{ __html: mobileSubtitle ?? subtitle }}
                style={{ color: accentColor }}
              />
            </div>
            {isCountdownBanner ? (
              <StickyBannerVoucherCopy voucher={voucherCode} />
            ) : (
              <Button<'a'> href={linkUrl} slots={{ root: 'a' }}>
                {linkText}
              </Button>
            )}
            {isCountdownBanner ? (
              <StickyBannerCountdown
                accentColor={accentColor}
                color={color}
                endDate={endDate}
              />
            ) : null}
          </div>
        </div>
        <button
          className="absolute right-2 top-2"
          onClick={(): void => {
            setBannerHidden(true)
          }}
          style={{ color }}
          type="button"
        >
          <IconClose
            className="text-2xl"
            // eslint-disable-next-line react/forbid-component-props -- the rule was disabled before a comment became required
            style={{ color }}
          />
        </button>
      </div>
    </div>
  )
}
